import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import routes from 'routes';
import { getPublicMeetSignup, getPublicMeetMyStatus } from 'api/publicMeetSignupApi';
import { getToken } from 'services/auth/tokenService';
import MetaTags from 'components/common/MetaTags';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import RegisterDialog from './RegisterDialog';

import splitFastLogo from 'assets/images/splitfast-logo-header.png';
import competitionMeetImage from 'assets/images/uicc.png';

import styles from './PublicMeetSignup.module.css';

// §6.1 — the PUBLIC, unauthenticated meet landing page (opaque short code). Phase A: read-only meet info + program +
// the window×state CTA (the registration funnel / inline auth come in later phases). First contact for many users, so
// it's a marketing-quality landing page, not an admin view.

// Meet date / window times are stored as local wall-clock in the meet's timezone (the "Olympics model" — shown as-is,
// no convert-to-my-local). An ISO string without a 'Z' parses as local time, so the displayed numbers match.
const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const capLine = (max) =>
  max != null ? `Choose up to ${max} event${max === 1 ? '' : 's'}` : 'No event limit';

const CTA_STYLE = { height: '46px', fontSize: '16px', padding: '0 30px', fontWeight: 600 };

const PublicMeetSignup = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [myStatus, setMyStatus] = useState(null); // logged-in caller's relationship to this meet (null = anon/unknown)
  const [dialog, setDialog] = useState(null); // the registration dialog: { email, step } | null

  // Deep link from the onboarding email (/m/:code?onboard=<email>) → re-open the dialog at the code step, prefilled, so
  // the registrant needn't keep the original tab. Only when not already logged in.
  useEffect(() => {
    const onboardEmail = searchParams.get('onboard');
    if (onboardEmail && !getToken()) {
      setDialog({ email: onboardEmail, step: 'onboard' });
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicMeetSignup(code);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ available: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // If a session exists, resolve our relationship to this meet so the CTA can deep-link into the app (§6.10).
    // Best-effort — on failure (e.g. an expired token) we just fall back to the logged-out CTA.
    if (getToken()) {
      (async () => {
        try {
          const res = await getPublicMeetMyStatus(code);
          if (!cancelled) setMyStatus(res.data);
        } catch {
          if (!cancelled) setMyStatus(null);
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [code]);

  const renderBody = () => {
    if (loading) return <div className={styles.centerMsg}>Loading…</div>;

    if (!data?.available) {
      return (
        <div className={styles.centerMsg}>
          <h1 className={styles.notAvailableTitle}>This registration isn’t available</h1>
          <p className={styles.notAvailableBody}>
            This sign-up link isn’t active. It may have closed, or the link may be incorrect — please
            check with the club that shared it.
          </p>
        </div>
      );
    }

    const loggedIn = !!getToken();
    const isRegistered = !!myStatus?.isRegistered;
    // Logged-in with host-club access (or an existing registration) → can use the §6.10 app page.
    const canManage =
      loggedIn && !!myStatus?.available && (myStatus.hasHostClubAccess || myStatus.isRegistered);

    // "Register" is window-gated; "Manage" is always reachable (the §6.10 page handles the window itself).
    const registerLive = data.windowState === 'Open' && !data.isFull;
    const ctaLabel = isRegistered ? 'Manage my registrations' : 'Register athletes';
    const ctaEnabled = isRegistered ? true : registerLive;

    const statusLine = isRegistered
      ? 'You’re registered for this meet'
      : data.windowState === 'BeforeOpen'
        ? `Registration opens ${fmtDateTime(data.signupOpenAt)}`
        : data.windowState === 'Closed'
          ? 'Registration is closed'
          : data.isFull
            ? 'Registration is full'
            : data.signupCloseAt
              ? `Open until ${fmtDateTime(data.signupCloseAt)}`
              : 'Registration is open';

    const onCtaClick = () => {
      if (canManage) {
        // Host-club member / already registered → the §6.10 self-service app page.
        navigate(routes.admin.myMeetRegistrations.url(myStatus.clubId, myStatus.meetId));
        return;
      }
      if (loggedIn && myStatus?.available) {
        // Logged in but not in the host club → their personal-portal My Registrations (Case 2). They already have an
        // account, so never the onboarding wizard.
        navigate(routes.personal.meetRegistrations.url(myStatus.meetId));
        return;
      }
      // Not logged in → open the registration dialog in place (§6.6). Email-first: a known account logs in inline; a
      // new email onboards via an emailed code. Either way they end up authenticated and routed from there.
      setDialog({ email: '', step: 'email' });
    };

    const renderCta = (onDark) => (
      <div className={styles.ctaBlock}>
        <CustomNewButton
          variant="primary2"
          disabled={!ctaEnabled}
          customStyle={CTA_STYLE}
          onClick={onCtaClick}
        >
          {ctaLabel}
        </CustomNewButton>
        <div
          className={`${styles.statusLine} ${
            ctaEnabled
              ? onDark
                ? styles.statusOpenDark
                : styles.statusOpen
              : onDark
                ? styles.statusMutedDark
                : styles.statusMuted
          }`}
        >
          {statusLine}
        </div>
      </div>
    );

    return (
      <>
        <section
          className={styles.hero}
          style={{
            backgroundImage: `linear-gradient(rgba(15,23,42,0.72), rgba(15,23,42,0.9)), url(${competitionMeetImage})`,
          }}
        >
          <div className={styles.heroInner}>
            {data.hostClubName && <div className={styles.heroHost}>{data.hostClubName}</div>}
            <h1 className={styles.heroTitle}>{data.meetName}</h1>
            <div className={styles.heroMeta}>
              {fmtDateTime(data.meetDate)}
              {data.location ? ` · ${data.location}` : ''}
            </div>
            {data.timeZone && (
              <div className={styles.heroTz}>Times shown in {data.timeZone}</div>
            )}
            {data.description && <p className={styles.heroAbout}>{data.description}</p>}
            {renderCta(true)}
          </div>
        </section>

        <main className={styles.body}>
          {data.program?.length > 0 && (
            <section className={styles.eventsSection}>
              <h2 className={styles.sectionTitle}>Events by age group</h2>
              <p className={styles.sectionHint}>
                Find an age group to see its events and how many each athlete can enter.
              </p>
              <div className={styles.eventGrid}>
                {data.program.map((g, i) => (
                  <div key={i} className={styles.ageCard}>
                    <div className={styles.ageCardHeader}>
                      <span className={styles.ageCardName}>{g.displayName}</span>
                      <span className={styles.ageCardCap}>{capLine(g.maxEvents)}</span>
                    </div>
                    <div className={styles.ageCardEvents}>
                      {g.events.map((e, j) => (
                        <span key={j} className={styles.eventTag}>
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className={styles.bottomCta}>
            <h2 className={styles.bottomCtaTitle}>Ready to register?</h2>
            {renderCta(false)}
          </section>
        </main>
      </>
    );
  };

  return (
    <div className={styles.page}>
      <MetaTags title="SplitFast | Meet Registration" />
      <header className={styles.topbar}>
        <a href="/" className={styles.logoLink} aria-label="SplitFast home">
          <img src={splitFastLogo} alt="SplitFast" className={styles.logo} />
        </a>
      </header>
      {renderBody()}
      <footer className={styles.footer}>Powered by SplitFast</footer>
      {dialog && (
        <RegisterDialog
          code={code}
          initialEmail={dialog.email}
          initialStep={dialog.step}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default PublicMeetSignup;
